import React, { useMemo, useState } from "react";
import { Phone, MessageCircle, Navigation, Check, UserX, Search, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { DeleteBookingButton } from "./AdminTools";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return ymd(d); };
const pill = (on) => `rounded-full px-3.5 py-1.5 text-[13px] transition-colors ${on ? "bg-[#183C35] text-[#F7F3EA]" : "bg-white border border-[#183C35]/15 text-[#183C35] hover:bg-[#F6F2E9]"}`;
const fmtDay = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const mapsUrl = (r) => r.dropoff
  ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${r.pickup}, Corfu`)}&destination=${encodeURIComponent(`${r.dropoff}, Corfu`)}`
  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${r.pickup}, Corfu`)}`;

// One entry per ride leg (a round trip gives two rides). Unpaid card bookings are not real rides.
const toRides = (bookings) => bookings.flatMap((b) => {
  if (b.pay === "card" && b.payment_status !== "paid") return [];
  const st = b.ride_status || {};
  const out = [{ b, leg: "out", date: b.date, time: b.time, pickup: b.pickup, dropoff: b.type === "hourly" ? null : b.dropoff, status: st.out || "scheduled" }];
  if (b.type === "round" && b.rdate) out.push({ b, leg: "return", date: b.rdate, time: b.rtime, pickup: b.dropoff, dropoff: b.pickup, status: st.return || "scheduled" });
  return out;
});

const VIEWS = [["today", "Today"], ["tomorrow", "Tomorrow"], ["week", "Next 7 days"], ["all", "All upcoming"], ["finished", "Finished · reviews"]];

const RideCard = ({ r, setBookings, onDeleted }) => {
  const { b } = r;
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/bookings/${b.ref}/ride-status`, { leg: r.leg, status: r.status === status ? "scheduled" : status });
      setBookings((l) => l.map((x) => (x.ref === b.ref ? data : x)));
    } catch { toast.error("Could not update ride"); } finally { setBusy(false); }
  };
  const sendReview = async () => {
    if (!window.confirm(`Send the review & discount email to ${b.email}?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/bookings/${b.ref}/review-email`); setBookings((l) => l.map((x) => (x.ref === b.ref ? data : x))); toast.success(`Sent to ${b.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  const tone = r.status === "done" ? "opacity-60" : r.status === "no_show" ? "opacity-60" : "";
  const phone = (b.phone || "").replace(/[^\d+]/g, "");
  return (
    <div className={`bg-white rounded-xl border border-[#183C35]/10 p-4 sm:p-5 grid sm:grid-cols-[110px_1fr_auto] gap-3 ${tone}`} data-testid={`ride-${b.ref}-${r.leg}`}>
      <div>
        <p className="font-serif-d text-[24px] leading-none text-[#183C35]">{r.time || "--:--"}</p>
        <p className="text-[12.5px] text-[#202522]/60 mt-1">{fmtDay(r.date)}</p>
        {r.leg === "return" && <span className="mt-1.5 inline-block text-[11px] px-2 py-0.5 rounded-full bg-[#F7F3EA] text-[#3E7F8E] font-medium">Return</span>}
      </div>
      <div className="text-[14px] min-w-0">
        <p className="font-medium text-[#183C35]">{b.name} <span className="text-[#202522]/50 font-normal">· {b.ref}</span></p>
        <p className="text-[#202522]/80 mt-0.5 break-words">{r.pickup} → {r.dropoff || `By the hour (${b.hours}h)`}</p>
        <p className="text-[12.5px] text-[#202522]/55 mt-0.5">{b.vehicle} · {b.pax} pax · {b.bags} bags{b.flight ? ` · Flight ${b.flight}` : ""} · {b.pay === "driver" ? `Driver collects €${b.total}` : `Paid online €${b.total}`}</p>
        {b.notes && <p className="text-[12.5px] text-[#202522]/55 mt-0.5 italic">“{b.notes}”</p>}
        <div className="mt-2.5 flex flex-wrap gap-2">
          <a href={`tel:${phone}`} className="inline-flex items-center gap-1 rounded-full border border-[#183C35]/15 px-3 py-1.5 text-[12.5px] text-[#183C35] hover:bg-[#F6F2E9]" data-testid={`ride-call-${b.ref}`}><Phone className="w-3.5 h-3.5" />Call</a>
          <a href={`https://wa.me/${phone.replace("+", "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-[#183C35]/15 px-3 py-1.5 text-[12.5px] text-[#183C35] hover:bg-[#F6F2E9]"><MessageCircle className="w-3.5 h-3.5" />WhatsApp</a>
          <a href={mapsUrl(r)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-[#183C35] text-[#F7F3EA] px-3 py-1.5 text-[12.5px] hover:bg-[#21524A]" data-testid={`ride-maps-${b.ref}-${r.leg}`}><Navigation className="w-3.5 h-3.5" />Open in Google Maps</a>
        </div>
      </div>
      <div className="flex sm:flex-col gap-2 sm:items-end flex-wrap">
        <button disabled={busy} onClick={() => setStatus("done")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12.5px] ${r.status === "done" ? "bg-[#E3F4EA] text-[#1C7A43]" : "border border-[#183C35]/15 text-[#183C35]"}`} data-testid={`ride-done-${b.ref}-${r.leg}`}><Check className="w-3.5 h-3.5" />{r.status === "done" ? "Done" : "Mark done"}</button>
        <button disabled={busy} onClick={() => setStatus("no_show")} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12.5px] ${r.status === "no_show" ? "bg-[#FDE8E8] text-[#B42318]" : "border border-[#183C35]/15 text-[#183C35]"}`} data-testid={`ride-noshow-${b.ref}-${r.leg}`}><UserX className="w-3.5 h-3.5" />{r.status === "no_show" ? "No-show" : "No-show"}</button>
        {r.leg === "out" && (b.review_email_status === "sent"
          ? <span className="text-[12px] text-[#1C7A43]">Review sent</span>
          : <button disabled={busy} onClick={sendReview} className="inline-flex items-center gap-1 rounded-full border border-[#183C35]/15 px-3 py-1.5 text-[12.5px] text-[#183C35]" data-testid={`ride-review-${b.ref}`}><Star className="w-3.5 h-3.5" />Send review request</button>)}
        <DeleteBookingButton b={b} onDeleted={onDeleted} />
      </div>
    </div>
  );
};

export const RidesPanel = ({ bookings, setBookings }) => {
  const [view, setView] = useState("today");
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const all = useMemo(() => toRides(bookings), [bookings]);
  const list = useMemo(() => {
    const t = addDays(0), tm = addDays(1), wk = addDays(7);
    return all.filter((r) => {
      if (q && !`${r.b.name} ${r.b.email} ${r.b.phone} ${r.b.ref} ${r.pickup} ${r.dropoff || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (date) return r.date === date;
      if (view === "today") return r.date === t;
      if (view === "tomorrow") return r.date === tm;
      if (view === "week") return r.date >= t && r.date <= wk;
      if (view === "all") return r.date >= t;
      return r.leg === "out" && (r.status === "done" || r.date < t) && r.status !== "no_show" && r.b.review_email_status !== "sent";
    }).sort((a, b) => (view === "finished" ? -1 : 1) * `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
  }, [all, view, q, date]);
  const count = (v) => all.filter((r) => (v === "today" ? r.date === addDays(0) : r.date === addDays(1))).length;
  const removeBooking = (ref) => setBookings((l) => l.filter((x) => x.ref !== ref));
  return (
    <div data-testid="rides-panel">
      <div className="flex flex-wrap gap-2 mb-3">
        {VIEWS.map(([k, l]) => <button key={k} onClick={() => { setView(k); setDate(""); }} className={pill(view === k && !date)} data-testid={`rides-view-${k}`}>{l}{k === "today" || k === "tomorrow" ? ` (${count(k)})` : ""}</button>)}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#202522]/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, place…" className="h-10 pl-9 rounded-full bg-white text-[16px] sm:text-[14px]" data-testid="rides-search" />
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-full border border-[#183C35]/15 bg-white px-4 text-[16px] sm:text-[14px]" data-testid="rides-date" />
        {(q || date) && <button onClick={() => { setQ(""); setDate(""); }} className="text-[13px] underline text-[#3E7F8E] self-start sm:self-center">Clear</button>}
      </div>
      {view === "finished" && !date && <p className="text-[13px] text-[#202522]/60 mb-3">Finished rides that haven't had a review request yet. Send one with a tap.</p>}
      <div className="space-y-3">
        {list.length === 0 ? <p className="py-10 text-center text-[#202522]/55">No rides here.</p>
          : list.map((r) => <RideCard key={`${r.b.ref}-${r.leg}`} r={r} setBookings={setBookings} onDeleted={removeBooking} />)}
      </div>
    </div>
  );
};
